import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  X,
  PlayCircle,
  FileText,
  Image,
  Download,
  Maximize2,
  Minimize2,
  ExternalLink,
} from "lucide-react";

const PlanModal = ({ medias, planName, onClose, pricingData, planTitles }) => {
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = "auto");
  }, []);

  const renderMediaContent = () => {
    const media = medias[activeMediaIndex];

    return (
      <div className="relative w-full h-full flex items-center justify-center bg-gray-50 rounded-lg">
        {media.type === "image" ? (
          <img
            src={media.url}
            alt={media.caption || planName}
            className="max-w-full max-h-full object-contain p-4"
            loading="lazy"
          />
        ) : media.type === "video" ? (
          <video
            src={media.url}
            className="w-full h-full object-contain rounded-lg"
            controls
            autoPlay
            playsInline
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-white">
            <FileText className="text-blue-500 mb-4" size={64} />
            <h3 className="text-xl font-medium text-gray-800 mb-3">
              PDF Document
            </h3>
            <p className="text-gray-600 mb-6 text-center max-w-md">
              This PDF document can be opened in a new tab or downloaded to your
              device.
            </p>
            <div className="flex gap-4">
              <a
                href={media.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ExternalLink size={18} />
                Open in New Tab
              </a>
              <a
                href={media.url}
                download
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                <Download size={18} />
                Download PDF
              </a>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        className={`bg-white rounded-xl shadow-2xl w-full max-w-6xl flex flex-col ${
          isFullscreen
            ? "fixed inset-0 max-w-none max-h-none rounded-none"
            : "max-h-[90vh]"
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 bg-gradient-to-r from-gray-800 to-gray-700 text-white rounded-t-xl">
          <h2 className="text-xl font-semibold tracking-tight">{planName}</h2>
          <div className="flex gap-3">
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {/* Media Container */}
          <div className="relative p-6">
            <div
              className={`${
                isFullscreen ? "h-[calc(100vh-220px)]" : "h-[560px]"
              }`}
            >
              {renderMediaContent()}

              {/* Navigation Arrows */}
              {medias.length > 1 && (
                <>
                  <button
                    onClick={() =>
                      setActiveMediaIndex(
                        (prev) => (prev - 1 + medias.length) % medias.length
                      )
                    }
                    className="absolute left-6 top-1/2 -translate-y-1/2 bg-black/30 text-white p-3 rounded-full hover:bg-black/50 backdrop-blur-sm"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={() =>
                      setActiveMediaIndex((prev) => (prev + 1) % medias.length)
                    }
                    className="absolute right-6 top-1/2 -translate-y-1/2 bg-black/30 text-white p-3 rounded-full hover:bg-black/50 backdrop-blur-sm"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail Strip */}
            {medias.length > 1 && (
              <div className="flex gap-3 mt-4 px-6 pb-2 overflow-x-auto">
                {medias.map((media, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveMediaIndex(index)}
                    className={`flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      activeMediaIndex === index
                        ? "border-blue-500 scale-105"
                        : "border-transparent opacity-75 hover:opacity-100"
                    }`}
                  >
                    {media.type === "pdf" ? (
                      <div className="w-full h-full bg-blue-50 flex items-center justify-center">
                        <FileText className="text-blue-500" size={20} />
                      </div>
                    ) : media.type === "video" ? (
                      <div className="w-full h-full bg-black flex items-center justify-center relative">
                        {media.thumbnailUrl ? (
                          <>
                            <img
                              src={media.thumbnailUrl}
                              className="w-full h-full object-cover opacity-80"
                              alt={`Video thumbnail ${index + 1}`}
                              loading="lazy"
                            />
                            <PlayCircle
                              className="absolute text-white"
                              size={20}
                            />
                          </>
                        ) : (
                          <>
                            <div className="w-full h-full bg-gray-800"></div>
                            <PlayCircle
                              className="absolute text-white"
                              size={20}
                            />
                          </>
                        )}
                      </div>
                    ) : (
                      <img
                        src={media.thumbnailUrl || media.url}
                        className="w-full h-full object-cover"
                        alt={`Thumbnail ${index + 1}`}
                        loading="lazy"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Pricing Table */}
          <div className="px-6 pb-8">
            <div className="border-t border-gray-200 pt-8">
              <h3 className="text-xl font-semibold mb-6 text-gray-800">
                Payment Schedule
              </h3>

              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        {planTitles?.category || "Category"}
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                        {planTitles?.standardPrice || "Standard Price"}
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                        {planTitles?.booking || "Booking (15%)"}
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                        {planTitles?.allocation || "Allocation (15%)"}
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                        {planTitles?.installments || "36 Installments"}
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                        {planTitles?.possession || "Possession (15%)"}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {pricingData.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {item.category}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700">
                          {item.standardPrice}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700">
                          {item.booking}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700">
                          {item.allocation}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700">
                          {item.installments}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700">
                          {item.possession}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
                Prices are subject to change. Contact us for the most current
                rates.
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Close
            </button>
            <button
              onClick={() =>
                window.open(
                  `mailto:info@hikshomes.com?subject=${planName} Inquiry`
                )
              }
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Request Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanModal;
